import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { collection, getDocs, query, where, updateDoc, doc } from "firebase/firestore";
import { firestore } from "../firebase.config";
import PageNavBar from "../components/PageNavBar";
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";
import {
  getUserByEmail,
  assignSupervisor,
  removeSupervisor,
  getSupervisedUsers,
  getTeamsForTenant,
  assignSupervisorToTeam,
  removeSupervisorFromTeam,
} from "../context/Api";

const SupervisorManagement = () => {
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const [userList, setUserList] = useState([]);
  const [supervisorAssignments, setSupervisorAssignments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [teams, setTeams] = useState([]);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeamSupervisor, setSelectedTeamSupervisor] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const effectiveTenantId =
    userRole === "SuperAdmin"
      ? selectedTenantId || sessionStorage.getItem("selectedTenantId")
      : tenantId || sessionStorage.getItem("selectedTenantId");

  // Fetch all users for the tenant
  useEffect(() => {
    const fetchUsers = async () => {
      if (!effectiveTenantId) return;

      try {
        const q = query(
          collection(firestore, "Users"),
          where("tenantId", "==", effectiveTenantId),
          where("Is_Active", "==", true)
        );
        const snapshot = await getDocs(q);
        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserList(users);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to fetch users");
      }
    };

    fetchUsers();
  }, [effectiveTenantId]);

  // Fetch all teams for the tenant
  useEffect(() => {
    const fetchTeams = async () => {
      if (!effectiveTenantId) return;

      try {
        const teamsList = await getTeamsForTenant(effectiveTenantId);
        setTeams(teamsList);
      } catch (error) {
        console.error("Error fetching teams:", error);
        setError("Failed to fetch teams");
      }
    };

    fetchTeams();
  }, [effectiveTenantId]);

  // Build supervisor assignments map
  useEffect(() => {
    const assignments = userList.map((user) => ({
      userEmail: user.userEmail,
      userName: user.userName,
      supervisorId: user.supervisorId || null,
      supervisorName: null,
      role: user.role,
    }));

    // Resolve supervisor names
    assignments.forEach((assignment) => {
      if (assignment.supervisorId) {
        const supervisor = userList.find((u) => u.userEmail === assignment.supervisorId);
        if (supervisor) {
          assignment.supervisorName = supervisor.userName;
        }
      }
    });

    setSupervisorAssignments(assignments);
  }, [userList]);

  const handleAssignSupervisor = async () => {
    if (!editingUser || !selectedSupervisor) {
      setError("Please select a supervisor");
      return;
    }

    // Prevent circular assignment
    if (editingUser.userEmail === selectedSupervisor) {
      setError("A user cannot be their own supervisor");
      return;
    }

    // Prevent assigning supervisor to SuperAdmin or Admin
    if (editingUser.role === "SuperAdmin" || editingUser.role === "admin") {
      setError("SuperAdmin and Admin roles cannot have supervisors");
      return;
    }

    try {
      await assignSupervisor(editingUser.userEmail, selectedSupervisor);
      setSuccess(`Supervisor assigned successfully`);
      
      // Refresh user list
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", effectiveTenantId),
        where("Is_Active", "==", true)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserList(users);
      
      setDialogOpen(false);
      setEditingUser(null);
      setSelectedSupervisor("");
      setError("");
    } catch (error) {
      console.error("Error assigning supervisor:", error);
      setError("Failed to assign supervisor");
    }
  };

  const handleRemoveSupervisor = async (userEmail) => {
    if (!window.confirm("Are you sure you want to remove the supervisor assignment?")) {
      return;
    }

    try {
      await removeSupervisor(userEmail);
      setSuccess("Supervisor removed successfully");
      
      // Refresh user list
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", effectiveTenantId),
        where("Is_Active", "==", true)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserList(users);
      
      setError("");
    } catch (error) {
      console.error("Error removing supervisor:", error);
      setError("Failed to remove supervisor");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setSelectedSupervisor(user.supervisorId || "");
    setDialogOpen(true);
    setError("");
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setSelectedSupervisor("");
    setError("");
  };

  // Get available supervisors (users who can be supervisors - exclude SuperAdmin and Admin from having supervisors)
  const getAvailableSupervisors = () => {
    return userList.filter(
      (user) =>
        user.userEmail !== editingUser?.userEmail &&
        user.userEmail !== editingTeam?.id &&
        user.role !== "SuperAdmin" &&
        (user.role === "user" || user.role === "admin" || user.role === "supervisor")
    );
  };

  // Handle team supervisor assignment
  const handleAssignTeamSupervisor = async () => {
    if (!editingTeam || !selectedTeamSupervisor) {
      setError("Please select a supervisor");
      return;
    }

    try {
      await assignSupervisorToTeam(editingTeam.id, selectedTeamSupervisor, effectiveTenantId);
      setSuccess(`Supervisor assigned to team successfully`);
      
      // Refresh teams
      const teamsList = await getTeamsForTenant(effectiveTenantId);
      setTeams(teamsList);
      
      // Refresh user list to show individual assignments
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", effectiveTenantId),
        where("Is_Active", "==", true)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserList(users);
      
      setTeamDialogOpen(false);
      setEditingTeam(null);
      setSelectedTeamSupervisor("");
      setError("");
    } catch (error) {
      console.error("Error assigning team supervisor:", error);
      setError("Failed to assign supervisor to team");
    }
  };

  const handleRemoveTeamSupervisor = async (teamId) => {
    if (!window.confirm("Are you sure you want to remove the supervisor from this team?")) {
      return;
    }

    try {
      await removeSupervisorFromTeam(teamId, effectiveTenantId);
      setSuccess("Team supervisor removed successfully");
      
      // Refresh teams
      const teamsList = await getTeamsForTenant(effectiveTenantId);
      setTeams(teamsList);
      
      // Refresh user list
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", effectiveTenantId),
        where("Is_Active", "==", true)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserList(users);
      
      setError("");
    } catch (error) {
      console.error("Error removing team supervisor:", error);
      setError("Failed to remove team supervisor");
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setSelectedTeamSupervisor(team.supervisorEmail || "");
    setTeamDialogOpen(true);
    setError("");
  };

  const handleCloseTeamDialog = () => {
    setTeamDialogOpen(false);
    setEditingTeam(null);
    setSelectedTeamSupervisor("");
    setError("");
  };

  // Filter out users who already have supervisors (to prevent deep hierarchies if needed)
  // For now, we allow hierarchies, so we don't filter

  if (userRole !== "admin" && userRole !== "SuperAdmin") {
    return (
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <PageNavBar title="Supervisor Management" backTo="/dashboard" />
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            Access Denied. Only Admins can manage supervisors.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
      }}
    >
      <PageNavBar title="Supervisor Management" backTo="/dashboard" />
      <TenantBanner />

      <Box sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, color: "white" }}>
          Supervisor Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Paper sx={{ mt: 3, mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Individual Assignments" />
            <Tab label="Team Assignments" />
          </Tabs>
        </Paper>

        {activeTab === 0 && (
        <Paper sx={{ overflowX: "auto", mt: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>User Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Supervisor</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisorAssignments.map((assignment) => (
                  <TableRow key={assignment.userEmail}>
                    <TableCell>{assignment.userName}</TableCell>
                    <TableCell>{assignment.userEmail}</TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.role}
                        size="small"
                        color={
                          assignment.role === "SuperAdmin"
                            ? "error"
                            : assignment.role === "admin"
                            ? "primary"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {assignment.supervisorName ? (
                        <Chip label={assignment.supervisorName} size="small" color="success" />
                      ) : (
                        <Chip label="No Supervisor" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(assignment)}
                      >
                        <EditIcon />
                      </IconButton>
                      {assignment.supervisorId && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSupervisor(assignment.userEmail)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        )}

        {activeTab === 1 && (
        <Paper sx={{ overflowX: "auto", mt: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>Team Name</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Supervisor</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell>{team.taskName || team.teamName || `Team ${team.id}`}</TableCell>
                    <TableCell>
                      {team.members && team.members.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {team.members.map((member, idx) => {
                            const user = userList.find(u => u.userEmail === member);
                            return (
                              <Chip
                                key={idx}
                                label={user ? user.userName : member}
                                size="small"
                                sx={{ mb: 0.5 }}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        "No members"
                      )}
                    </TableCell>
                    <TableCell>
                      {team.supervisorEmail ? (
                        <Chip 
                          label={userList.find(u => u.userEmail === team.supervisorEmail)?.userName || team.supervisorEmail} 
                          size="small" 
                          color="success" 
                        />
                      ) : (
                        <Chip label="No Supervisor" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditTeam(team)}
                      >
                        <EditIcon />
                      </IconButton>
                      {team.supervisorEmail && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTeamSupervisor(team.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        )}

        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingUser ? `Assign Supervisor for ${editingUser.userName}` : "Assign Supervisor"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {editingUser && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Current User: <strong>{editingUser.userName}</strong> ({editingUser.userEmail})
                  </Typography>
                  {(editingUser.role === "SuperAdmin" || editingUser.role === "admin") && (
                    <Alert severity="info">
                      {editingUser.role === "SuperAdmin"
                        ? "SuperAdmin cannot have supervisors. Their bookings are auto-approved."
                        : "Admin cannot have supervisors. Their bookings are auto-approved."}
                    </Alert>
                  )}
                </>
              )}
              <FormControl fullWidth>
                <InputLabel>Select Supervisor</InputLabel>
                <Select
                  value={selectedSupervisor}
                  label="Select Supervisor"
                  onChange={(e) => setSelectedSupervisor(e.target.value)}
                  disabled={
                    editingUser &&
                    (editingUser.role === "SuperAdmin" || editingUser.role === "admin")
                  }
                >
                  <MenuItem value="">
                    <em>No Supervisor (Auto-approve)</em>
                  </MenuItem>
                  {getAvailableSupervisors().map((user) => (
                    <MenuItem key={user.userEmail} value={user.userEmail}>
                      {user.userName} ({user.userEmail}) - {user.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAssignSupervisor}
              disabled={
                editingUser &&
                (editingUser.role === "SuperAdmin" || editingUser.role === "admin")
              }
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={teamDialogOpen} onClose={handleCloseTeamDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTeam ? `Assign Supervisor to Team: ${editingTeam.taskName || editingTeam.teamName || editingTeam.id}` : "Assign Team Supervisor"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {editingTeam && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Team: <strong>{editingTeam.taskName || editingTeam.teamName || editingTeam.id}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Members: {editingTeam.members?.join(", ") || "No members"}
                  </Typography>
                  <Alert severity="info">
                    Assigning a supervisor to a team will also assign them individually to each team member.
                  </Alert>
                </>
              )}
              <FormControl fullWidth>
                <InputLabel>Select Supervisor</InputLabel>
                <Select
                  value={selectedTeamSupervisor}
                  label="Select Supervisor"
                  onChange={(e) => setSelectedTeamSupervisor(e.target.value)}
                >
                  <MenuItem value="">
                    <em>No Supervisor (Auto-approve)</em>
                  </MenuItem>
                  {getAvailableSupervisors().map((user) => (
                    <MenuItem key={user.userEmail} value={user.userEmail}>
                      {user.userName} ({user.userEmail}) - {user.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTeamDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAssignTeamSupervisor}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SupervisorManagement;


